import {Component, output, input, computed, signal, effect, inject, untracked, OnDestroy} from '@angular/core';
import {ModeGestionNavette} from '../model/mode-gestion-navette.enum';
import {TrajetService} from '../../../../shared/services/trajet.service';
import {TrajetDto} from '../../../../shared/dto/trajet.dto';
import 'moment/locale/fr';
import {TrajetSelectionneService} from '../services/trajet-selectionne.service';
import {AlertService, ApplicationErreur} from 'sigale-angular';
import {finalize} from 'rxjs/operators';
import {FormulaireFiltrageTrajets} from '../model/formulaire-filtrage-trajets';
import {DifferencePeriode} from '../../../../shared/models/difference-periode.model';
import {TrajetFiltrageService} from '../../../../shared/services/trajet-filtrage.service';
import {ModeNavette} from '../../../../shared/models/mode-navette.enum';

@Component({
	selector: 'app-liste-trajets',
	templateUrl: './liste-trajets.component.html',
	styleUrl: './liste-trajets.component.scss'
})
export class ListeTrajetsComponent implements OnDestroy {
	// Constantes
	readonly MODE_NAVETTE = ModeNavette;
	readonly NOM_ALERTE_TRAJETS = 'alerteTabTrajets';

	// Injections
	private alertService = inject(AlertService);
	private trajetFiltrageService = inject(TrajetFiltrageService);
	private trajetSelectionneService = inject(TrajetSelectionneService);
	private trajetService = inject(TrajetService);

	// Inputs
	modeGestionNavette = input.required<ModeGestionNavette>();

	// Outputs
	basculerEnModeCreationModificationTrajet = output<void>();

	// Signaux
	enChargement = signal(false);
	filtres = signal<FormulaireFiltrageTrajets>(new FormulaireFiltrageTrajets());

	/**
	 * Map des trajets par date (clé = date, valeur = liste de trajets).
	 */
	trajets = signal<TrajetDto[]>([]);

	// Variables
	suggestionsLieuxAutocomplete: string[] = [];
	private differencePeriode = new DifferencePeriode(0, null);

	// Computed
	afficherTrajetsPasses = computed(() => this.filtres().afficherTrajetsPasses ?? false);
	boutonsDesactives = computed(() => this.modeGestionNavette() === ModeGestionNavette.CREATION_MODIFICATION);
	messageAucunTrajet = computed(() =>
		this.trajets().length === 0
			? 'GESTION_NAVETTE.TRAJETS.AUCUN_TRAJET'
			: 'GESTION_NAVETTE.TRAJETS.AUCUN_TRAJET_FILTRES'
	);
	trajetsFiltres = computed(() => this.trajetFiltrageService.filtrerTrajetsParCriteresGestionNavette(this.trajets(), this.filtres(), this.afficherTrajetsPasses()));

	constructor() {
		this.recupererTrajets();
		this.ecouterChangementsTrajetsFiltres();
	}

	ngOnDestroy() {
		this.trajetSelectionneService.deselectionnerTrajet();
	}

	/**
	 * Écoute les changements dans les trajets filtrés et désélectionne le trajet sélectionné si celui-ci n'est plus présent dans la liste des trajets filtrés.
	 */
	ecouterChangementsTrajetsFiltres(): void {
		effect(() => {
			const trajetsFiltres = this.trajetsFiltres();
			const trajetSelectionne = untracked(() => this.trajetSelectionneService.trajetSelectionne());

			const trajetPresentDansListeTrajetsFiltres = trajetsFiltres.find(trajet => trajet.id === trajetSelectionne?.id);

			if (!trajetPresentDansListeTrajetsFiltres) {
				this.trajetSelectionneService.deselectionnerTrajet();
			}
		}, { allowSignalWrites: true });
	}

	/**
	 * Bascule l'autre partie de l'écran en mode création.
	 */
	onCreerClick(): void {
		this.trajetSelectionneService.deselectionnerTrajet();
		this.basculerEnModeCreationModificationTrajet.emit();
	}

	/**
	 * Change la semaine affichée et récupère les trajets pour cette semaine.
	 *
	 * @param periode objet contenant la différence de semaines par rapport à la semaine actuelle.
	 */
	changerSemaine(periode: DifferencePeriode): void {
		this.differencePeriode = periode;
		this.recupererTrajets();
	}

	/**
	 * Récupère les trajets pour la semaine sélectionnée.
	 */
	recupererTrajets(): void {
		this.enChargement.set(true);
		this.trajetService.recupererTrajets(this.differencePeriode.differenceNombreSemaines, this.differencePeriode.differenceNombreJours, ModeNavette.GESTION).pipe(
			finalize(() => this.enChargement.set(false))
		).subscribe({
			next: (trajets: TrajetDto[]) => {
				this.trajetSelectionneService.actualiserTrajetSelectionne(trajets);
				this.suggestionsLieuxAutocomplete = this.trajetFiltrageService.recupererLieuxDepartArrivee(trajets);
				this.trajets.set(trajets);
			},
			error: (erreur: ApplicationErreur) => {
				this.alertService.clear(this.NOM_ALERTE_TRAJETS);
				this.alertService.error(erreur, this.NOM_ALERTE_TRAJETS);
			}
		});
	}

	/**
	 * Récupère les filtres appliqués dans le menu de filtrage et les stocke dans le signal correspondant.
	 *
	 * @param filtresFormulaire la liste des filtres appliqués.
	 */
	recupererFiltres(filtresFormulaire: FormulaireFiltrageTrajets): void {
		this.filtres.set(filtresFormulaire);
	}

	/**
	 * Méthode appelée quand les filtres ont été réinitialisés dans le menu de filtrage.
	 */
	reinitialiserFiltres(): void {
		this.filtres.set(new FormulaireFiltrageTrajets());
	}
}
